// 👁 ADMIN — istalgan foydalanuvchining emulatorini jonli ko'rish va «✋ Boshqaruvni olish» (2026-10-26).
// /emu-view?user=<id>&slot=<n>&label=… — Admin → Emulatorlar ro'yxatidagi «👁 Ko'rish» alohida oynada ochadi
// (mijozga ko'rsatish uchun ham: ⛶ to'liq ekran). Backend: socket `asUser` (faqat role=admin), boshqaruv olinsa
// egasining bosishlari rad etiladi va AI agent pauzada turadi; oyna yopilsa qulf 15 s da o'zi ochiladi.
// App.js monolitidagi emulator to'riga tegilmaydi — o'zining socket'i va kichik holati bor. docs/knowledge/184.
import React, { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { attachConfirmBlink } from './confirmBlink';   // 🔔 tasdiq kutilsa yorliq sarlavhasi miltillaydi
import { useEmuAudio, EmuAudioBtn } from './emuAudio';   // 🔊 emulator ovozi (backend 204)

const LONGPRESS_MS = 480;

export default function AdminEmuViewer() {
  const { user } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const target = Number(params.get('user')) || 0;
  const slot = Number(params.get('slot')) || 0;
  const label = params.get('label') || `user ${target} · Emulator ${slot + 1}`;

  const socketRef = useRef(null);
  const frameUrlRef = useRef(null);
  const dragRef = useRef(null);
  const boxRef = useRef(null);
  const controlRef = useRef(false);
  const [frame, setFrame] = useState(null);
  const [lastFrameAt, setLastFrameAt] = useState(0);
  const [connected, setConnected] = useState(false);
  const [control, setControl] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [status, setStatus] = useState('');
  const [text, setText] = useState('');
  const [tick, setTick] = useState(0);
  // 🔊 Ovozni eshitish boshqaruvsiz ham mumkin (faqat tinglash); server asUser'ni faqat admin uchun qabul qiladi.
  const { audio, toggleAudio } = useEmuAudio(socketRef, { asUser: target, notify: setNote });

  const isAdmin = user && user.role === 'admin';
  const base = { slot, asUser: target };

  useEffect(() => { document.title = `👁 ${label}`; }, [label]);
  useEffect(() => { const t = setInterval(() => setTick(x => x + 1), 2000); return () => clearInterval(t); }, []);

  useEffect(() => {
    if (!isAdmin || !target) return undefined;
    const token = localStorage.getItem('khorezmai_token') || '';
    const socket = io(process.env.REACT_APP_API_URL ?? '', { auth: { token } });
    socketRef.current = socket;
    attachConfirmBlink(socket);   // 🔔 kutilayotgan tasdiq — sarlavha miltillaydi
    const start = () => socket.emit('emulator:start_stream', { slot, asUser: target });
    socket.on('connect', () => {
      setConnected(true);
      start();
      // Qayta ulanishda boshqaruv saqlansin (eski socket qulfi 15 s ichida bo'shardi).
      if (controlRef.current) socket.emit('emulator:admin_control', { slot, asUser: target, action: 'take' }, (r) => { if (!r || !r.ok) { controlRef.current = false; setControl(false); } });
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('emulator:frame', (p) => {
      if (!p || (p.slot || 0) !== slot) return;
      const d = p.data;
      let src;
      if (typeof d === 'string') src = `data:image/png;base64,${d}`;
      else if (d) {
        try { src = URL.createObjectURL(new Blob([d], { type: p.fmt === 'jpg' ? 'image/jpeg' : 'image/png' })); } catch { return; }
        const old = frameUrlRef.current; frameUrlRef.current = src;
        if (old && old.startsWith('blob:')) setTimeout(() => { try { URL.revokeObjectURL(old); } catch {} }, 2000);
      } else return;
      setFrame(src); setLastFrameAt(Date.now());
    });
    // Harakatsizlikda server oqimni to'xtatadi (auto-lock) — ko'rish oynasi ochiq va ko'rinib tursa qayta boshlaymiz.
    socket.on('emulator:stream_idle_stop', (p) => { if ((p && p.slot) === slot && !document.hidden) setTimeout(start, 500); });
    socket.on('emulator:status', (p) => { if (p && Number(p.userId) === target && (p.slot || 0) === slot) { setStatus(p.status || ''); if (p.status === 'running') start(); } });
    socket.on('emulator:admin_lock', (p) => {
      if (!p || Number(p.userId) !== target || (p.slot || 0) !== slot) return;
      if (!p.locked && controlRef.current) { controlRef.current = false; setControl(false); setNote('🔓 Boshqaruv qaytarildi'); }
    });
    socket.on('emulator:locked', (p) => setNote(`🔒 ${p && p.by ? p.by : 'Boshqa administrator'} boshqaryapti`));
    const onVis = () => { if (document.hidden) socket.emit('emulator:stop_stream', { slot }); else start(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      try { if (controlRef.current) socket.emit('emulator:admin_control', { slot, asUser: target, action: 'release' }); } catch {}
      try { socket.emit('emulator:stop_stream', { slot }); } catch {}
      socket.disconnect();
    };
  }, [isAdmin, target, slot]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleControl = () => {
    const s = socketRef.current; if (!s) return;
    const action = control ? 'release' : 'take';
    setBusy(true);
    s.emit('emulator:admin_control', { slot, asUser: target, action }, (r) => {
      setBusy(false);
      if (!r || !r.ok) { setNote(`⚠️ ${(r && r.error) || "Bajarib bo'lmadi"}`); return; }
      controlRef.current = !!r.locked; setControl(!!r.locked);
      setNote(r.locked ? "✋ Boshqaruv sizda — egasi hozir faqat ko'radi, AI agent kutadi" : '🔓 Boshqaruv egasiga qaytdi');
    });
  };

  // Kadr ichidagi haqiqiy (letterbox'siz) maydon → qurilma koordinatasi.
  const toDevice = (clientX, clientY) => {
    const img = boxRef.current && boxRef.current.querySelector('img');
    if (!img) return null;
    const b = img.getBoundingClientRect();
    const dw = img.naturalWidth || 720, dh = img.naturalHeight || 1280;
    const sc = Math.min(b.width / dw, b.height / dh);
    const cw = dw * sc, ch = dh * sc;
    const left = b.left + (b.width - cw) / 2, top = b.top + (b.height - ch) / 2;
    return { x: (clientX - left) * (dw / cw), y: (clientY - top) * (dh / ch), scale: dw / cw };
  };
  const send = (ev, payload) => socketRef.current && socketRef.current.emit(ev, { ...base, ...payload });
  const onDown = (e) => {
    if (!control) return;
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
    const lp = setTimeout(() => {
      const d = dragRef.current; if (!d || d.moved || d.fired) return;
      d.fired = true; const p = toDevice(d.x, d.y); if (p) send('emulator:longpress', { x: p.x, y: p.y });
    }, LONGPRESS_MS);
    dragRef.current = { x: e.clientX, y: e.clientY, t: Date.now(), moved: false, fired: false, lp };
  };
  const onMove = (e) => {
    const d = dragRef.current; if (!d || d.fired) return;
    const p = toDevice(e.clientX, e.clientY);
    if (p && Math.hypot(e.clientX - d.x, e.clientY - d.y) * p.scale > 28) d.moved = true;
  };
  const onUp = (e) => {
    const d = dragRef.current; dragRef.current = null; if (!d) return;
    clearTimeout(d.lp);
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    if (d.fired || !control) return;
    const to = toDevice(e.clientX, e.clientY); if (!to) return;
    if (d.moved) { const from = toDevice(d.x, d.y); if (from) send('emulator:swipe', { x1: from.x, y1: from.y, x2: to.x, y2: to.y, duration: Date.now() - d.t }); }
    else send('emulator:tap', { x: to.x, y: to.y });
  };
  const key = (k) => control && send('emulator:keyevent', { keycode: k });
  const sendText = () => { if (!control || !text) return; send('emulator:text', { text }); setText(''); };
  const fullscreen = () => { const el = document.getElementById('kh-emu-view'); if (el && el.requestFullscreen) el.requestFullscreen().catch(() => {}); };

  if (!isAdmin) return <div className="p-8 text-center text-gray-500">Bu sahifa faqat administrator uchun.</div>;
  if (!target) return <div className="p-8 text-center text-gray-500">Emulator ko'rsatilmagan (?user=&slot=).</div>;

  const live = lastFrameAt && Date.now() - lastFrameAt < 15000;
  void tick;
  const btn = 'px-3 py-1.5 rounded-lg text-sm font-medium transition disabled:opacity-40';
  return (
    <div id="kh-emu-view" className="min-h-screen bg-gray-950 text-gray-100 flex flex-col items-center p-3 gap-2">
      <div className="w-full max-w-xl flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="font-semibold truncate" translate="no">👁 {label}</div>
          <div className="text-xs text-gray-400">
            {connected ? (live ? '🟢 jonli' : (frame ? '⏸ ekran o\'zgarmayapti' : '⏳ kutilmoqda…')) : '🔌 ulanmoqda…'}
            {status === 'stopped' && ' · emulator o\'chirildi'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={toggleControl} disabled={busy || !connected}
            title={control ? "Boshqaruvni egasiga qaytarish" : "Boshqaruvni olish — egasi va AI agent shu payt emulatorni boshqara olmaydi"}
            className={`${btn} ${control ? 'bg-amber-500 hover:bg-amber-600 text-black' : 'bg-primary-700 hover:bg-primary-800 text-white'}`}>
            {busy ? '⏳' : control ? '🔓 Boshqaruvni qaytarish' : '✋ Boshqaruvni olish'}
          </button>
          <EmuAudioBtn slot={slot} audio={audio} onToggle={toggleAudio} className={btn} offCls="bg-white/10 hover:bg-white/20" />
          <button onClick={fullscreen} title="To'liq ekran (mijozga ko'rsatish uchun)" className={`${btn} bg-white/10 hover:bg-white/20`}>⛶</button>
        </div>
      </div>
      {note && <div className="w-full max-w-xl text-xs text-amber-300">{note}</div>}
      <div ref={boxRef}
        className={`relative flex-1 w-full flex items-center justify-center select-none ${control ? 'cursor-pointer' : 'cursor-default'}`}
        style={{ touchAction: 'none', minHeight: 300 }}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={() => { dragRef.current = null; }}>
        {frame
          ? <img src={frame} alt="emulator" draggable={false} className="max-h-[calc(100vh-150px)] max-w-full object-contain rounded-xl border border-white/10" />
          : <div className="text-gray-500 text-sm">{connected ? "Kadr kutilmoqda… (emulator ishlab turganiga ishonch hosil qiling)" : "Ulanmoqda…"}</div>}
        {!control && frame && <div className="absolute bottom-2 left-1/2 -translate-x-1/2 text-[11px] bg-black/60 px-2 py-1 rounded">👁 faqat ko'rish — boshqarish uchun «✋ Boshqaruvni olish»</div>}
      </div>
      <div className="w-full max-w-xl flex items-center gap-2 flex-wrap justify-center">
        <button onClick={() => key(4)} disabled={!control} title="Orqaga" className={`${btn} bg-white/10 hover:bg-white/20`}>←</button>
        <button onClick={() => key(3)} disabled={!control} title="Bosh ekran" className={`${btn} bg-white/10 hover:bg-white/20`}>⌂</button>
        <button onClick={() => key(187)} disabled={!control} title="Oxirgi ilovalar" className={`${btn} bg-white/10 hover:bg-white/20`}>▣</button>
        <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') sendText(); }} disabled={!control}
          placeholder="Matn yozish…" className="flex-1 min-w-[140px] px-3 py-1.5 rounded-lg bg-white/10 border border-white/10 text-sm disabled:opacity-40" />
        <button onClick={sendText} disabled={!control || !text} className={`${btn} bg-white/10 hover:bg-white/20`}>↵</button>
      </div>
    </div>
  );
}
